import { alpha, createTheme } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#3434EF',
    },
    secondary: {
      main: '#000000',
    },
    info: {
      main: '#FF7442',
    },
    success: {
      main: '#06a779',
    },
  },
  color: {
    background: {
      light: '#F2F1EB',
      standard: '#FEFDF5',
      dark: '#000000',
      inverse: '#000000',
    },
    line: {
      light: '#212730',
      standard: '#2f3744',
    },
    soft: {
      green: '#123633',
      red: '#341E23',
      yellow: 'rgba(235, 167, 58, .15)',
      blue: 'rgba(88, 166, 255, .15)',
    },
    telnyx: {
      citron: '#D3FFA6',
      primary: '#3434EF',
      green: '#00E3AA',
      orange: '#eb8758',
      blue: '#3434EF',
      red: '#f35c5c',
      yellow: '#eba73a',
      purple: '#9a90e9',
      error: '#f44336',
      grey: '#161B22',
      deepblue: '#3434EF',
      black: '#000000',
      disabled: 'rgba(0, 0, 0, 0.26)',
    },
    grey: {
      light: '#282D33',
      standard: '#282D33',
      heavy: '#282D33',
      text: '#8b949e',
      textLight: '#6e7681',
    },
  },
  font: {
    size: {
      xs: '1.2ch',
      sm: '1.5ch',
      md: '1.8ch',
      lg: '2ch',
      xl: '2.4ch',
      xxl: '3ch',
    },
  },
  shape: {
    pillRadius: 50,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1700,
    },
  },
  components: {
    MuiList: {
      styleOverrides: {
        root: {
          paddingTop: 0,
          paddingBottom: 0,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          paddingRight: '0.75rem !important',
        },
        root: {
          fontFamily: 'Inter !important',
          fontSize: '14px !important',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: '#E6E3D3',
        },
        root: {
          borderRadius: '6px',
          fontFamily: 'Inter !important',
          fontSize: '14px !important',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: 'Inter',
          fontSize: '14px !important',
          fontWeight: 400,
          backgroundColor: '#11161e',
          color: 'white',
          padding: '0.5rem 0.75rem',
        },
        arrow: {
          color: '#11161e',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          backgroundColor: '#FEFDF5',
          '& .MuiListItemText-secondary': {
            color: 'rgb(110,118,129)',
            fontSize: '1.4ch',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#000000',
          borderRadius: '6px',
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          fontFamily: 'Inter',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        startIcon: {
          marginLeft: '-2px',
        },
        endIcon: {
          marginRight: 0,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          backgroundColor: '#FEFDF5',
          '& .MuiListItemText-secondary': {
            color: 'rgb(110,118,129)',
            fontSize: '1.4ch',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: '30px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          backgroundImage: 'none',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FEFDF5 !important',
          backgroundImage: 'none',
          borderRadius: '16px',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#E6E3D3',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: '#000000',
          fontFamily: 'Inter !important',
          fontSize: '14px !important',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FEFDF5 !important',
          backgroundImage: 'none',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.selected &&
            !ownerState.multiple && {
              backgroundColor: '#3434EF !important',
              color: '#fff',
            }),
        }),
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            color: '#8b949e',
          },
        },
        underline: {
          '&:hover:not(.Mui-disabled):before': {
            borderColor: '#32373D',
          },
          '&:after': {
            borderColor: '#3434EF',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardSuccess: {
          svg: {
            color: '#00c08b',
          },
        },
        standardInfo: {
          backgroundColor: '#0400ff10',
          svg: {
            color: '#3434EF',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: 'Inter !important',
        },
        icon: {
          marginLeft: '10px',
        },
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        toolbarContainer: {
          button: {
            color: '#3434EF',
            padding: '0.25rem 0.5rem',
          },
          'button:hover': {
            backgroundColor: alpha('#3434EF', 0.12),
          },
        },
        overlay: {
          backgroundColor: '#FEFDF5 !important',
        },
        columnHeader: {
          padding: '0 20px !important',
        },
        columnHeaderCheckbox: {
          padding: '0px !important',
        },
        cell: {
          padding: '0 20px !important',
        },
        root: {
          fontFamily: 'Inter !important',
          backgroundColor: '#FEFDF5 !important',
          '& .MuiDataGrid-cell': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-cell--editing:focus-within': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
            userSelect: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus': {
            outline: 'none',
            userSelect: 'none',
          },
          '& .MuiDataGrid-cell:hover': {
            cursor: 'pointer',
          },
        },
      },
    },
  },
});

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00E3AA',
    },
    secondary: {
      main: '#000000',
    },
    info: {
      main: '#FF7442',
    },
    success: {
      main: '#00E3AA',
    },
  },
  color: {
    background: {
      light: '#191f27',
      standard: '#0f141b',
      dark: '#0d1117',
      inverse: '#ffffff',
    },
    line: {
      light: '#212730',
      standard: '#2f3744',
    },
    soft: {
      green: '#123633',
      red: '#341E23',
      yellow: 'rgba(235, 167, 58, .15)',
      blue: 'rgba(88, 166, 255, .15)',
    },
    telnyx: {
      citron: '#D3FFA6',
      primary: '#00E3AA',
      green: '#00E3AA',
      orange: '#eb8758',
      blue: '#3434EF',
      red: '#f35c5c',
      yellow: '#eba73a',
      purple: '#9a90e9',
      error: '#f44336',
      grey: '#161B22',
      black: '#000000',
      deepblue: '#3434EF',
      disabled: 'rgba(255, 255, 255, 0.26)',
    },
    grey: {
      light: '#282D33',
      standard: '#282D33',
      heavy: '#282D33',
      text: '#8b949e',
      textLight: '#6e7681',
    },
  },
  font: {
    size: {
      xs: '1.2ch',
      sm: '1.5ch',
      md: '1.8ch',
      lg: '2ch',
      xl: '2.4ch',
      xxl: '3ch',
    },
  },
  shape: {
    pillRadius: 50,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1700,
    },
  },
  components: {
    MuiList: {
      styleOverrides: {
        root: {
          paddingTop: 0,
          paddingBottom: 0,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        startIcon: {
          marginLeft: '-2px',
        },
        endIcon: {
          marginRight: 0,
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          backgroundImage: 'none',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#FFFFFF',
          borderRadius: '6px',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1F252D !important',
          backgroundImage: 'none',
          borderRadius: '16px',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          fontFamily: 'Inter !important',
          fontSize: '14px !important',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#0d1117 !important',
          backgroundImage: 'none',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardInfo: {
          backgroundColor: '#292B4380',
          borderColor: '#383b62 !important',
          color: '#bec0e3',
          svg: {
            color: '#bec0e3',
          },
        },
        standardWarning: {
          backgroundColor: '#53431e80',
          borderColor: '#383b62 !important',
          color: '#e1b59d',
          svg: {
            color: '#e1b59d',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: 'Inter',
          fontSize: '14px !important',
          fontWeight: 400,
          backgroundColor: '#11161e',
          color: 'white',
          padding: '0.5rem 0.75rem',
        },
        arrow: {
          color: '#11161e',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: '30px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: 'Inter !important',
        },
        icon: {
          marginLeft: '10px',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          backgroundColor: '#0f141b',
          '& .MuiListItemText-secondary': {
            color: 'rgb(110,118,129)',
            fontSize: '1.4ch',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          backgroundColor: '#0f141b',
          '& .MuiListItemText-secondary': {
            color: 'rgb(110,118,129)',
            fontSize: '1.4ch',
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            color: '#8b949e',
          },
        },
        root: {
          '::before': {
            borderBottom: '1px solid #2f3744',
          },
        },
        underline: {
          '&:hover:not(.Mui-disabled):before': {
            borderColor: '#32373D',
          },
          '&:after': {
            borderColor: '#00E3AA',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#2f3744',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          paddingRight: '0.75rem !important',
        },
        root: {
          fontFamily: 'Inter !important',
          fontSize: '14px !important',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: '#2f3744',
        },
        root: {
          backgroundColor: '#161b22 !important',
          borderRadius: '6px',
          fontFamily: 'Inter !important',
          fontSize: '14px !important',
          '&:focus-within .MuiOutlinedInput-notchedOutline': {
            borderColor: '#00E3AA !important',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0b7e62',
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        popper: {
          '& ::-webkit-scrollbar': { width: '8px', height: '8px' },
          '& ::-webkit-scrollbar-track': { background: '#424242 !important' },
          '& ::-webkit-scrollbar-thumb': { background: '#686868 !important' },
        },
        listbox: {
          backgroundColor: '#161b22',
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        toolbarContainer: {
          button: {
            padding: '0.25rem 0.5rem',
          },
        },
        columnHeader: {
          padding: '0 20px !important',
        },
        columnHeaderCheckbox: {
          padding: '0px !important',
        },
        cell: {
          padding: '0 20px !important',
        },
        overlay: {
          backgroundColor: '#161b22 !important',
        },
        root: {
          fontFamily: 'Inter !important',
          backgroundColor: '#161b22 !important',
          borderColor: '#2f3744 !important',
          '& .MuiDataGrid-withBorderColor': {
            borderColor: '#2f3744 !important',
          },
          '& .MuiDataGrid-cell': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-cell--editing:focus-within': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
            userSelect: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus': {
            outline: 'none',
            userSelect: 'none',
          },
          '& .MuiDataGrid-cell:hover': {
            cursor: 'pointer',
          },
        },
      },
    },
  },
});

export default lightTheme;
